import React from 'react';
import { useAuth } from '../utils/AuthContext';
import { Settings as SettingsIcon, Shield, AlertCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import UserList from '../components/UserList';

const Settings: React.FC = () => {
  const { user } = useAuth();

  // Check if user is admin
  const isAdmin = user?.user_type === 'Admin';

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6 sm:p-10">
        <div className="relative mb-8">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-red-100 rounded-2xl flex items-center justify-center shadow-xl">
            <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-red-600" />
          </div>
          <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-8 sm:h-8 bg-red-400 rounded-full flex items-center justify-center shadow-lg">
            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
          </div>
        </div>
        
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
          Access Restricted
        </h1>
        
        <p className="text-lg sm:text-xl text-gray-600 mb-2 max-w-lg">
          Settings are only available to administrators
        </p>
        
        <p className="text-base text-gray-500 mb-8 max-w-md">
          You need admin privileges to access system settings and user management.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 max-w-md">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-left">
              <p className="text-sm font-medium text-amber-800">Current Access Level</p>
              <p className="text-sm text-amber-700">
                {user?.user_type || 'User'} - Limited access
              </p>
            </div>
          </div>
        </div>
        
        <Link
          to="/"
          className="btn-primary mb-10 hover:shadow-lg hover:scale-105 transition-all duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        
        <div className="card max-w-md sm:max-w-lg w-full">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
            Need Admin Access?
          </h3>
          <ul className="text-left text-gray-600 space-y-2 text-sm sm:text-base">
            <li className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
              <span>Contact your system administrator</span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
              <span>Request role upgrade if needed</span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
              <span>Admin can manage user permissions</span>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">Manage system settings and user accounts</p>
          </div>
        </div>

        {/* Admin Badge */}
        <div className="flex items-center space-x-2 bg-purple-100 text-purple-700 px-3 py-2 rounded-lg">
          <Shield className="w-4 h-4" />
          <span className="text-sm font-medium">Admin Access</span>
        </div>
      </div>

      {/* Admin Welcome Message */}
      <div className="bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg p-6 border border-primary-200">
        <div className="flex items-start space-x-4">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Welcome, Administrator
            </h2>
            <p className="text-gray-700 mb-3">
              You have full access to system settings and user management. Use these tools to manage users, configure system settings, and maintain the platform.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-white text-primary-700 text-xs px-2 py-1 rounded-full border border-primary-200">
                User Management
              </span>
              <span className="bg-white text-purple-700 text-xs px-2 py-1 rounded-full border border-purple-200">
                System Settings
              </span>
              <span className="bg-white text-green-700 text-xs px-2 py-1 rounded-full border border-green-200">
                Full Access
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="space-y-8">
        {/* User Management Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
            <p className="text-gray-600 mt-1">Create, manage, and control user access to the system</p>
          </div>
          
          <div className="p-6">
            <UserList />
          </div>
        </div>

        {/* System Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Current User</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Phone:</strong> {user?.phone}</p>
                <p><strong>Role:</strong> {user?.user_type}</p>
                <p><strong>Name:</strong> {user?.name}</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">System Status</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Database:</strong> <span className="text-green-600">Connected</span></p>
                <p><strong>Authentication:</strong> <span className="text-green-600">Active</span></p>
                <p><strong>Version:</strong> 1.0.0</p>
              </div>
            </div>
          </div>
        </div>

        {/* Future Settings Placeholder */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Settings</h2>
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <SettingsIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">More Settings Coming Soon</h3>
            <p className="text-gray-600">
              Additional system configuration options will be available in future updates.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
